import { useEffect, useMemo, useRef, useState } from "react";
import { Crosshair, MapPin, Navigation } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { fileUrl } from "@/lib/api";

const FALLBACK_CENTER = [47.1539, 15.6622];
const LOCAL_RADIUS_KM = 10;

function coordinate(value, axis) {
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) return null;
  const parsed = Number(normalized);
  const limit = axis === "lat" ? 90 : 180;
  return Number.isFinite(parsed) && Math.abs(parsed) <= limit ? parsed : null;
}

function distanceFromStRuprecht(lat, lng) {
  const toRadians = (value) => (value * Math.PI) / 180;
  const [centerLat, centerLng] = FALLBACK_CENTER;
  const deltaLat = toRadians(lat - centerLat);
  const deltaLng = toRadians(lng - centerLng);
  const a = Math.sin(deltaLat / 2) ** 2
    + Math.cos(toRadians(centerLat)) * Math.cos(toRadians(lat)) * Math.sin(deltaLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function directionsUrl(station) {
  const lat = coordinate(station?.latitude, "lat");
  const lng = coordinate(station?.longitude, "lng");
  if (lat == null || lng == null) return null;
  const destination = `${lat},${lng}`;
  const appleDevice = /iPad|iPhone|Macintosh/.test(navigator.userAgent);
  return appleDevice
    ? `https://maps.apple.com/?daddr=${encodeURIComponent(destination)}&dirflg=w`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&travelmode=walking`;
}

export default function InteractiveRouteMap({ stations = [], completedStationIds = [] }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const locationLayerRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [locationState, setLocationState] = useState("idle");

  const completed = useMemo(() => new Set(completedStationIds), [completedStationIds]);
  const locationData = useMemo(() => {
    const all = stations.map((station, index) => ({
        ...station,
        routeNumber: index + 1,
        lat: coordinate(station.latitude, "lat"),
        lng: coordinate(station.longitude, "lng"),
      }));
    const valid = all.filter((station) => station.lat != null && station.lng != null);
    return {
      mappedStations: valid.filter((station) => distanceFromStRuprecht(station.lat, station.lng) <= LOCAL_RADIUS_KM),
      missingCount: all.length - valid.length,
      outOfAreaCount: valid.filter((station) => distanceFromStRuprecht(station.lat, station.lng) > LOCAL_RADIUS_KM).length,
    };
  }, [stations]);
  const { mappedStations, missingCount, outOfAreaCount } = locationData;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const map = L.map(containerRef.current, {
      zoomControl: true,
      scrollWheelZoom: false,
      attributionControl: true,
    });
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    const points = mappedStations.map((station) => [station.lat, station.lng]);
    if (points.length > 1) {
      map.fitBounds(L.latLngBounds(points), { padding: [34, 34], maxZoom: 16 });
    } else if (points.length === 1) {
      map.setView(points[0], 16);
    } else {
      map.setView(FALLBACK_CENTER, 14);
    }

    mappedStations.forEach((station) => {
      const isDone = completed.has(station.id);
      const icon = L.divIcon({
        className: "spuren-map-marker-wrap",
        html: `<span class="spuren-map-marker"><b>${station.routeNumber}</b>${isDone ? '<i aria-hidden="true">✓</i>' : ""}</span>`,
        iconSize: [42, 48],
        iconAnchor: [21, 45],
      });
      const marker = L.marker([station.lat, station.lng], { icon, title: station.title }).addTo(map);

      const popup = document.createElement("div");
      popup.className = "spuren-map-popup";
      if (station.hero_image) {
        const image = document.createElement("img");
        image.src = fileUrl(station.hero_image);
        image.alt = "";
        popup.appendChild(image);
      }
      const label = document.createElement("span");
      label.textContent = `Spur ${String(station.routeNumber).padStart(2, "0")}`;
      popup.appendChild(label);
      const title = document.createElement("strong");
      title.textContent = station.title;
      popup.appendChild(title);
      if (station.location_description) {
        const description = document.createElement("p");
        description.textContent = station.location_description;
        popup.appendChild(description);
      }
      const openButton = document.createElement("button");
      openButton.type = "button";
      openButton.textContent = "Station ansehen";
      openButton.addEventListener("click", () => {
        document.getElementById(`station-${station.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        map.closePopup();
      });
      popup.appendChild(openButton);

      marker.bindPopup(popup, { closeButton: false, offset: [0, -34], minWidth: 190 });
      marker.on("click", () => setSelected(station));
    });

    window.setTimeout(() => map.invalidateSize(), 0);
    return () => {
      map.remove();
      mapRef.current = null;
      locationLayerRef.current = null;
    };
  }, [mappedStations, completed]);

  const locate = () => {
    if (!navigator.geolocation || !mapRef.current) {
      setLocationState("unsupported");
      return;
    }
    setLocationState("loading");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const map = mapRef.current;
        if (!map) return;
        if (locationLayerRef.current) locationLayerRef.current.remove();
        const group = L.layerGroup().addTo(map);
        L.circle([coords.latitude, coords.longitude], {
          radius: Math.max(coords.accuracy, 8),
          color: "#3478f6",
          fillColor: "#3478f6",
          fillOpacity: 0.12,
          weight: 1,
        }).addTo(group);
        L.circleMarker([coords.latitude, coords.longitude], {
          radius: 8,
          color: "#fff",
          fillColor: "#3478f6",
          fillOpacity: 1,
          weight: 3,
        }).bindTooltip("Du bist hier", { permanent: false }).addTo(group);
        locationLayerRef.current = group;
        map.flyTo([coords.latitude, coords.longitude], Math.max(map.getZoom(), 16), { duration: 0.8 });
        setLocationState("found");
      },
      () => setLocationState("denied"),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 15000 }
    );
  };

  const navigationUrl = selected ? directionsUrl(selected) : null;

  return (
    <section className="interactive-route-map" aria-labelledby="interactive-map-title" data-testid="interactive-route-map">
      <div className="interactive-route-map__header">
        <div>
          <p className="eyebrow !text-[#cbd7cf]">Unterwegs orientieren</p>
          <h2 id="interactive-map-title" className="mt-1 text-3xl text-white">Die sechs Stationen auf einer Karte</h2>
          <p className="mt-2 max-w-lg text-sm leading-relaxed text-[#d7e0d9]">
            Alle sechs Stationen auf einen Blick. Zoome, tippe eine Nummer an oder zeige deinen Standort. Den genauen Fußweg findest du weiterhin bei den einzelnen Etappen.
          </p>
        </div>
        <button type="button" className="interactive-route-map__locate" onClick={locate} disabled={locationState === "loading"}>
          <Crosshair className="h-4 w-4" />
          {locationState === "loading" ? "Standort wird gesucht …" : "Wo bin ich?"}
        </button>
      </div>

      <div ref={containerRef} className="interactive-route-map__canvas" aria-label="Interaktive Karte der sechs SPUREN-Stationen ohne eingezeichneten Fußweg" />

      <div className="interactive-route-map__legend" aria-label="Kartenerklärung">
        <span><i className="interactive-route-map__legend-pin">1</i> Station</span>
        <span><i className="interactive-route-map__legend-check">✓</i> bereits besucht</span>
      </div>

      {missingCount > 0 ? (
        <p className="interactive-route-map__notice">
          <MapPin className="h-4 w-4" /> Noch {missingCount} Stationsposition(en) im Adminbereich ergänzen.
        </p>
      ) : null}
      {outOfAreaCount > 0 ? (
        <p className="interactive-route-map__notice interactive-route-map__notice--warning">
          <MapPin className="h-4 w-4" /> {outOfAreaCount} Position(en) liegen nicht bei St. Ruprecht und werden bis zur Korrektur nicht angezeigt.
        </p>
      ) : null}
      {locationState === "denied" ? <p className="interactive-route-map__notice">Standortzugriff wurde nicht erlaubt. Die Karte funktioniert trotzdem.</p> : null}
      {locationState === "unsupported" ? <p className="interactive-route-map__notice">Dieses Gerät unterstützt keine Standortanzeige.</p> : null}

      {selected ? (
        <div className="interactive-route-map__selection">
          <div>
            <span>Spur {String(selected.routeNumber).padStart(2, "0")}</span>
            <strong>{selected.title}</strong>
          </div>
          {navigationUrl ? (
            <a href={navigationUrl} target="_blank" rel="noreferrer">
              <Navigation className="h-4 w-4" /> Route dorthin
            </a>
          ) : null}
        </div>
      ) : (
        <p className="interactive-route-map__hint">Tippe auf eine Zahl, um die Station auszuwählen und dorthin zu navigieren.</p>
      )}
    </section>
  );
}
